import { EntityTypeId } from "../../../../core/entity/EntityTypeId.enum"
import { sample } from "../../../../core/rng/index"
import type { RNGConfig } from "../../../../core/rng/RNGConfig.type"

// Warm gray sandstone
export const desertBoulderSandstone = 0xA89F91 // #A89F91

// Pale yellowish rock (limestone / weathered)
export const desertBoulderLimestone = 0xCBBF9D // #CBBF9D

// Reddish-brown oxidized rock (iron-rich)
export const desertBoulderRust = 0x9A6E4B // #9A6E4B

// Dark gray basalt or volcanic rock
export const desertBoulderBasalt = 0x5A5A5A // #5A5A5A

// Light tan granite or dry rock
export const desertBoulderGranite = 0xD4C4AA // #D4C4AA

export const desertBoulderColors = [
  desertBoulderSandstone,
  desertBoulderLimestone,
  desertBoulderRust,
  desertBoulderBasalt,
  desertBoulderGranite,
]

/** Moss-covered stone — common in damp forest floors */
export const forestBoulderMoss = 0x6C7A5B // #6C7A5B

/** Damp earth-gray granite — neutral and cool */
export const forestBoulderGranite = 0x8B8C85 // #8B8C85

/** Lichen-speckled rock — warm gray with organic growth */
export const forestBoulderLichen = 0xA9A88D // #A9A88D

/** Dark wet stone — shaded or under dense canopy */
export const forestBoulderShadow = 0x50524F // #50524F

/** Brownish sedimentary rock — blended with soil */
export const forestBoulderMudstone = 0x7A6451 // #7A6451

export const forestBoulderColors = [
  forestBoulderMoss,
  forestBoulderGranite,
  forestBoulderLichen,
  forestBoulderShadow,
  forestBoulderMudstone,
]

/** Cold gray granite — common in alpine and high-altitude areas */
export const mountainBoulderGranite = 0x8E8F91 // #8E8F91

/** Blue-gray schist or slate — gives a cooler, high-altitude tone */
export const mountainBoulderSlate = 0x6B717A // #6B717A

/** Light silicate rock — chalky, sun-bleached areas */
export const mountainBoulderChalk = 0xD1CEC7 // #D1CEC7

/** Dark igneous rock — exposed volcanic or metamorphic faces */
export const mountainBoulderBasalt = 0x4A4A4A // #4A4A4A

/** Rust-streaked cliffstone — mineral-rich outcrops (iron oxidation) */
export const mountainBoulderIronVein = 0x946C53 // #946C53

export const mountainBoulderColors = [
  mountainBoulderGranite,
  mountainBoulderSlate,
  mountainBoulderChalk,
  mountainBoulderBasalt,
  mountainBoulderIronVein,
]

/** Soft gray fieldstone — common in grassy lowlands */
export const plainsBoulderFieldstone = 0x9D9D91 // #9D9D91

/** Mossy-tinted granite — stone with lichen or moss growth */
export const plainsBoulderMossyGranite = 0x7F876E // #7F876E

/** Pale earthy rock — low contrast against dry or sunlit grass */
export const plainsBoulderEarthy = 0xC8BDA2 // #C8BDA2

/** Warm beige weathered stone — frequent in eroded open terrain */
export const plainsBoulderBeige = 0xB6AA8F // #B6AA8F

/** Dull brown-gray stone — neutral color for common glacial rocks */
export const plainsBoulderGlacial = 0x888378 // #888378

export const plainsBoulderColors = [
  plainsBoulderFieldstone,
  plainsBoulderMossyGranite,
  plainsBoulderEarthy,
  plainsBoulderBeige,
  plainsBoulderGlacial,
]

/** Pale sandy beige — sunlit seafloor near beaches or atolls */
export const seaBasinSand = 0xD6CBB5 // #D6CBB5

/** Submerged algae green — common in shallow, nutrient-rich areas */
export const seaBasinAlgae = 0x728C6B // #728C6B

/** Cool mineral gray — rocky seabed or shelf */
export const seaBasinRock = 0x5C6D75 // #5C6D75

/** Deep silt blue — soft muddy basins in deeper or calmer waters */
export const seaBasinSilt = 0x667B87 // #667B87

/** Coral tan — areas with calcified remains or bleached coral debris */
export const seaBasinCoral = 0xC9B9A6 // #C9B9A6

export const seaBasinColors = [
  seaBasinSand,
  seaBasinAlgae,
  seaBasinRock,
  seaBasinSilt,
  seaBasinCoral,
]

/** Damp, cool sand-colored rock — tidal-washed limestone or sandstone */
export const shoalBoulderWetSandstone = 0xB4AA95 // #B4AA95

/** Algae-tinted stone — slight green hue from marine influence */
export const shoalBoulderAlgaeStone = 0x7C8B76 // #7C8B76

/** Pale beachstone — rounded, dry surface stones */
export const shoalBoulderBeachstone = 0xD6C8AF // #D6C8AF

/** Weathered drift rock — sun-bleached and wave-worn */
export const shoalBoulderDriftRock = 0xA8A7A1 // #A8A7A1

/** Brine-darkened rock — saturated from tide pools or salt spray */
export const shoalBoulderBrineDark = 0x6F6C61 // #6F6C61

export const shoalBoulderColors = [
  shoalBoulderWetSandstone,
  shoalBoulderAlgaeStone,
  shoalBoulderBeachstone,
  shoalBoulderDriftRock,
  shoalBoulderBrineDark,
]

/** Icy light gray granite — common in glacial environments */
export const snowBoulderGranite = 0xDDE0E3 // #DDE0E3

/** Cold slate or schist — a bluish-gray metamorphic rock */
export const snowBoulderSlate = 0x7B8A9B // #7B8A9B

/** Frosty limestone — pale and lightly tinted beige/gray */
export const snowBoulderLimestone = 0xCFCFC4 // #CFCFC4

/** Snow-covered dark rock — exposed basalt or gneiss */
export const snowBoulderBasalt = 0x4A4F52 // #4A4F52

/** Weathered ice-stained rock — cold purple-gray hue */
export const snowBoulderFrostbite = 0x9A9FB3 // #9A9FB3

export const snowBoulderColors = [
  snowBoulderGranite,
  snowBoulderSlate,
  snowBoulderLimestone,
  snowBoulderBasalt,
  snowBoulderFrostbite,
]

export function getBoulderColorHexNumber(etype_id: number, rng: RNGConfig): number {
  if (etype_id === EntityTypeId.Desert) {
    return sample(desertBoulderColors, rng)!
  }
  if (etype_id === EntityTypeId.Forest) {
    return sample(forestBoulderColors, rng)!
  }
  if (etype_id === EntityTypeId.Mountain) {
    return sample(mountainBoulderColors, rng)!
  }
  if (etype_id === EntityTypeId.Plain) {
    return sample(plainsBoulderColors, rng)!
  }
  if (etype_id === EntityTypeId.Sea || etype_id === EntityTypeId.River) {
    return sample(seaBasinColors, rng)!
  }
  if (etype_id === EntityTypeId.Shoal) {
    return sample(shoalBoulderColors, rng)!
  }
  if (etype_id === EntityTypeId.Snow) {
    return sample(snowBoulderColors, rng)!
  }
  return 0xff0000
}
